/**
 * This helper module contains standard CRUD actions that can be used across most entity types, including
 * system (accounts, productions, etc.) and data entry entity types (APs, JEs, PCs, POs, and Checks).
 * Each action must receive the store as its first arg (since this is Vuex), and a payload as the second.
 * The payload may be an object or a string, and must contain the appropriate Vuex mutations to be called
 * so that the action operates on the appropriate store module's state. For any action that fetches data
 * from the API, the payload must contain the endpoint.
 */
import Vue from 'vue'
import { normalize, schema } from 'normalizr'
import { checkStatus, getHeaders, parseJSON, buildUrl, buildUrlWithQuery } from '@/utils/api'
import handleXHRError from '@/utils/helpers/HandleXHRError'
import { RECEIVE_FORM_FIELDS } from '../../mutationTypes'

/**
 * Generates normalizr schema
 * @param opts
 * @return {*}
 */
const generateSchema = (opts) => {
  const { normalizrSchema, name, listSchemaRequired, subSchemas, idAttribute } = opts
  let schema
  if (subSchemas && Object.keys(subSchemas).length) {
    schema = new normalizrSchema.Entity(name, subSchemas)
  } else if (idAttribute) {
    schema = new normalizrSchema.Entity(name, undefined, {
      idAttribute: (value) => value[idAttribute] || value.id
    })
  } else {
    schema = new normalizrSchema.Entity(name)
  }
  if (listSchemaRequired) {
    return { schema, listSchema: new normalizrSchema.Array(schema) }
  }
  return schema
}

/**
 * Generates object containing subschemas
 * @param additionalMutations
 * @return {{}}
 */
const generateSubSchemas = (additionalMutations) => {
  const subSchemas = {}
  for (const name in additionalMutations) {
    subSchemas[name] = generateSchema({
      normalizrSchema: schema,
      name: additionalMutations[name].endpoint,
      listSchemaRequired: additionalMutations[name].listSchemaRequired
    })
  }
  return subSchemas
}

const actions = {
  /**
   * Fetches the first page of a given entity type
   * Note: this will likely be deprecated in favor of using fetchWithQuery without query string parameters
   * @param store
   * @param payload
   * @return {Promise.<TResult>}
   */
  fetch (store, payload) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    const {
      receivingMutation,
      receiveMutation,
      receiveMetadataMutation,
      receiveFailureMutation,
      nested,
      endpoint,
      idAttribute
    } = payload
    if (receivingMutation) this.commit(receivingMutation, true)
    return Vue.http.get(buildUrl(endpoint, null, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        if (receivingMutation) this.commit(receivingMutation, false)
        if (!r.hasOwnProperty('results')) return Promise.reject(`Error fetching ${endpoint}`)
        const { results, next, previous, count } = r
        const normalized = normalize(results, generateSchema({
          normalizrSchema: schema,
          name: endpoint,
          listSchemaRequired: true,
          idAttribute,
          subSchemas: nested && Object.keys(nested).length ? generateSubSchemas(nested) : null
        }).listSchema)
        const { entities, result } = normalized
        if (nested && Object.keys(nested).length) {
          for (const name in nested) {
            this.commit(nested[name].receiveMutation, { data: entities[nested[name].endpoint] })
          }
        }
        this.commit(receiveMutation, { page: 1, data: entities[endpoint] })
        this.commit(receiveMetadataMutation, { next, previous, count, result: result.length })
        return entities
      })
      .catch(e => {
        console.log(e)
        if (receivingMutation) this.commit(receivingMutation, false)
        if (receiveFailureMutation) this.commit(receiveFailureMutation, handleXHRError(e))
        return e
      })
  },
  /**
   * Fetches a given entity type with query
   * @param store
   * @param payload
   * @return {Promise.<TResult>}
   */
  fetchWithQuery (store, payload) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    const {
      receivingMutation,
      receiveMutation,
      receiveMetadataMutation,
      receiveFailureMutation,
      resetMetaMutation,
      nested,
      endpoint,
      query
    } = payload
    if (receivingMutation) this.commit(receivingMutation, true)
    return Vue.http.get(buildUrlWithQuery(endpoint, query, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        if (receivingMutation) this.commit(receivingMutation, false)
        this.commit(resetMetaMutation)
        if (!r.hasOwnProperty('results')) return Promise.reject(`Error fetching ${endpoint}`)
        const { results, next, previous, count } = r
        const normalized = normalize(results, generateSchema({
          normalizrSchema: schema,
          name: endpoint,
          listSchemaRequired: true,
          subSchemas: nested && Object.keys(nested).length ? generateSubSchemas(nested) : null
        }).listSchema)
        const { entities, result } = normalized
        if (nested && Object.keys(nested).length) {
          for (const name in nested) {
            this.commit(nested[name].receiveMutation, { data: entities[nested[name].endpoint] })
          }
        }
        const mutationPayload = { data: entities[endpoint] }
        if (query && query.page && typeof query.page === 'number') { // If query contains a page number
          mutationPayload.page = query.page // pass the page number to the mutation handler
        }
        this.commit(receiveMutation, mutationPayload)
        this.commit(receiveMetadataMutation, { next, previous, count, result: result.length })
        return { query, count, entities }
      })
      .catch(e => {
        console.log(e)
        if (receivingMutation) this.commit(receivingMutation, false)
        if (receiveFailureMutation) this.commit(receiveFailureMutation, handleXHRError(e))
        return e
      })
  },
  /**
   * Retrieves entities of a given type by page number. If not already in the entity type module's cache,
   * fetches them by page number.
   * @param store
   * @param payload
   * @return {*}
   */
  fetchByPage (store, payload) {
    const {
      page,
      query,
      moduleName,
      receivingMutation,
      receiveMutation,
      receiveMetadataMutation,
      receiveFailureMutation,
      nested,
      endpoint,
      idAttribute
    } = payload
    const { listSchema } = generateSchema({
      normalizrSchema: schema,
      name: endpoint,
      listSchemaRequired: true,
      idAttribute,
      subSchemas: nested && Object.keys(nested).length ? generateSubSchemas(nested) : null
    })
    const { cache } = store.rootState[moduleName]
    const instanceUrl = this.getters.getActiveInstance.url
    const url = (type = null) => {
      let queryObj
      if (!type) {
        queryObj = query ? { page, ...query } : { page }
        return buildUrlWithQuery(endpoint, queryObj, instanceUrl)
      } else if (type === 'next') {
        queryObj = query ? { page: page + 1, ...query } : { page: page + 1 }
        return (page + 1) > Math.ceil(store.rootState[moduleName].count / 20)
          ? null
          : buildUrlWithQuery(endpoint, queryObj, instanceUrl)
      } else if (type === 'previous') {
        queryObj = query ? { page: page - 1, ...query } : { page: page - 1 }
        return (page - 1) <= 0
          ? null
          : buildUrlWithQuery(endpoint, queryObj, instanceUrl)
      }
    }
    // Check if data is already cached
    const c = Object.values(cache).filter(v => v.page && parseInt(v.page) === parseInt(page))
    if (!(payload && payload.skipCacheCheck) && c && c.length && c.length <= 20) {
      const e = normalize(c, listSchema)
      this.commit(receiveMutation, { page, data: e.entities[endpoint] })
      this.commit(receiveMetadataMutation, { next: url('next'), previous: url('previous'), result: e.result.length })
      return e
    }
    // If not, fetch
    if (receivingMutation) this.commit(receivingMutation, true)
    return Vue.http.get(url(), { headers: getHeaders(this.getters.getToken) })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        if (receivingMutation) this.commit(receivingMutation, false)
        if (!r) return Promise.reject(`Error fetching ${endpoint}`)
        const { results, next, previous, count } = r
        const { entities, result } = normalize(results, listSchema)
        if (nested && Object.keys(nested).length) {
          for (const name in nested) {
            this.commit(nested[name].receiveMutation, { data: entities[nested[name].endpoint] })
          }
        }
        this.commit(receiveMutation, { page, data: entities[endpoint] })
        this.commit(receiveMetadataMutation, { next, previous, count, result: result.length })
        return entities
      })
      .catch(e => {
        console.log(e)
        if (receivingMutation) this.commit(receivingMutation, false)
        if (receiveFailureMutation) this.commit(receiveFailureMutation, handleXHRError(e))
        return e
      })
  },
  /**
   * Makes an OPTIONS call to a given endpoint
   * @param store
   * @param payload
   * @return {Promise.<TResult>}
   */
  fetchOptions (store, payload) {
    const { query, endpoint } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    return Vue.http({ url: buildUrl(endpoint, query || null, instanceUrl), headers, method: 'options' })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        if (!r) return Promise.reject()
        if (r.actions && r.actions.POST) {
          this.commit(RECEIVE_FORM_FIELDS, { endpoint, fields: r.actions.POST })
        }
        return r
      })
      .catch(e => {
        console.log(e)
        return handleXHRError(e)
      })
  },
  /**
   * Saves a given model
   * @param store
   * @param payload
   * @return {Promise.<TResult>}
   */
  save (store, payload) {
    const { data, savingMutation, receiveMutation, saveFailureMutation, incrementMutation, nested, endpoint } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(savingMutation, true)
    if (saveFailureMutation) this.commit(saveFailureMutation, null)
    return Vue.http.post(buildUrl(endpoint, null, instanceUrl), data, { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(savingMutation, false)
        if (!r) return Promise.reject(`Error saving ${endpoint}`)
        const { entities } = normalize(r, generateSchema({
          normalizrSchema: schema,
          name: endpoint,
          listSchemaRequired: true,
          subSchemas: nested && Object.keys(nested).length ? generateSubSchemas(nested) : null
        }).schema)
        if (nested && Object.keys(nested).length) {
          for (const name in nested) {
            this.commit(nested[name].receiveMutation, { data: entities[nested[name].endpoint] })
          }
        }
        this.commit(receiveMutation, { data: entities[endpoint], isNewModel: true })
        this.commit(incrementMutation, 1)
        return r
      })
      .catch(e => {
        this.commit(savingMutation, false)
        if (saveFailureMutation) this.commit(saveFailureMutation, handleXHRError(e))
        return e
      })
  },
  /**
   * Updates a given model
   * @param store
   * @param payload
   * @return {Promise.<T>}
   */
  update (store, payload) {
    const { id, data, receiveMutation, updatingMutation, updateFailureMutation, nested, endpoint } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(updatingMutation, id)
    if (updateFailureMutation) this.commit(updateFailureMutation, { id: '', err: '' })
    return Vue.http.patch(buildUrl(`${endpoint}/${id}`, null, instanceUrl), data, { headers })
      .then(r => {
        this.commit(updatingMutation, null)
        if (r && r.status === 200) {
          const { entities } = normalize(r.body, generateSchema({
            normalizrSchema: schema,
            name: endpoint,
            listSchemaRequired: true,
            subSchemas: nested && Object.keys(nested).length ? generateSubSchemas(nested) : null
          }).schema)
          if (nested && Object.keys(nested).length) {
            for (const name in nested) {
              this.commit(nested[name].receiveMutation, { data: entities[nested[name].endpoint] })
            }
          }
          this.commit(receiveMutation, { data: entities[endpoint], isUpdatedModel: true })
          return r.body
        } else {
          return Promise.reject(r)
        }
      })
      .catch(e => {
        this.commit(updatingMutation, null)
        if (updateFailureMutation) this.commit(updateFailureMutation, { id, err: handleXHRError(e, `Failed to update ${endpoint}`) })
        return e
      })
  },
  /**
   * Deletes a given model
   * @param store
   * @param payload
   * @return {Promise.<TResult>}
   */
  delete (store, payload) {
    const { id, deleteMutation, deletingMutation, deleteFailureMutation, endpoint } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(deletingMutation, true)
    if (deleteFailureMutation) this.commit(deleteFailureMutation, { id: '', err: '' })
    return Vue.http.delete(buildUrl(`${endpoint}/${id}`, null, instanceUrl), { headers })
      .then(checkStatus)
      .then(() => {
        this.commit(deletingMutation, false)
        this.commit(deleteMutation, payload)
        return payload
      })
      .catch(e => {
        console.log(e)
        this.commit(deletingMutation, false)
        if (deleteFailureMutation) this.commit(deleteFailureMutation, { id: payload, err: e || `Error deleting ${endpoint}` })
        return e
      })
  },
  /**
   * Clears the state in a given entity type module
   * @param store
   * @param payload
   */
  clearLocalState (store, payload) {
    const { clearMutation, clearCache } = payload
    this.commit(clearMutation, clearCache)
  },
  /**
   * Resets metadata in a given entity type module
   * @param store
   * @param payload
   * @return {*|void}
   */
  resetMeta (store, payload) {
    if (payload && payload.resetMetaMutation) {
      return this.commit(payload.resetMetaMutation)
    }
  },
  fetchLines (store, payload) {
    // TODO: you should write this method out because clearly fetchLinesByTransactionId isn't working.
  }
}

export default {
  actions
}



// WEBPACK FOOTER //
// ./src/store/modules/helpers/actions.js